import { z } from 'zod';
import { ABILITY_KEYS, DOMAIN_TYPES, DOMAIN_VERIFICATION_STATUSES, GYM_STATUSES, LATE_ONLINE_PAYMENT_MODES, PERMISSIONS, PERMISSION_GRANTS, PERMISSION_MATRIX_ROLES, PERMISSION_MODULES, PERMISSION_RISKS, PERMISSION_SOURCES, ROLES, SESSION_ONLINE_PAYMENT_MODES, SHIFT_ACCESS_MODES, SHIFT_ROLES, SHIFT_SWAP_MODES, STAFF_ROLES, SWITCHABLE_ROLES } from '@bulk/types';
import {
  currencySchema,
  idSchema,
  localeSchema,
  patchOf,
  slugSchema,
  timezoneSchema,
  timeHHMM,
  weekdaySchema,
} from './common';
import { kbSettingsSchema } from './knowledge';
import { recruitingSettingsSchema } from './recruiting';
import { reviewSettingsSchema } from './reviews';

export const brandingSchema = z.object({
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#f2c94c'),
  secondaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#111111'),
  backgroundColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#0b0b0b'),
  textColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#f5f5f5'),
  fontFamily: z.string().max(80).default('Inter'),
  logoAssetId: idSchema.nullable().default(null),
  faviconAssetId: idSchema.nullable().default(null),
});
export type Branding = z.infer<typeof brandingSchema>;

export const openingHoursSchema = z.record(
  z.string(),
  z.object({ open: timeHHMM, close: timeHHMM }).nullable(),
);

export const bookingPolicySchema = z.object({
  bookingOpensHoursBefore: z.number().int().min(1).max(24 * 60).default(24 * 7),
  bookingClosesMinutesBefore: z.number().int().min(0).max(24 * 60).default(0),
  cancellationCutoffMinutes: z.number().int().min(0).max(24 * 60 * 7).default(120),
  lateCancellationCountsAsNoShow: z.boolean().default(true),
  maxActiveBookingsPerMember: z.number().int().min(1).max(100).default(10),
  waitlistEnabled: z.boolean().default(true),
});
export type BookingPolicy = z.infer<typeof bookingPolicySchema>;

/** One-to-one sessions: how far ahead and how late members book, and when a cancelled package session goes back. */
export const appointmentPolicySchema = z.object({
  bookingHorizonDays: z.number().int().min(1).max(90).default(30),
  minNoticeMinutes: z.number().int().min(0).max(7 * 24 * 60).default(120).describe('Members book no closer to the start than this'),
  cancellationWindowHours: z.number().int().min(0).max(7 * 24).default(24).describe('A package session cancelled later than this before it starts stays used'),
  reminderMinutesBefore: z.number().int().min(0).max(24 * 60).default(120).describe('0 sends no reminder'),
  hostApproval: z.boolean().default(false).describe("A member's booking of a trainer's or a massage therapist's time waits for their yes; an appointment type can override it"),
  requestTtlHours: z.number().int().min(1).max(7 * 24).default(24).describe('A request nobody answers expires this long after it was made'),
  requestCutoffMinutes: z.number().int().min(0).max(7 * 24 * 60).default(120).describe('A request nobody answers also expires this long before the session starts, if that comes first'),
  onlinePayment: z
    .enum(SESSION_ONLINE_PAYMENT_MODES)
    .default('OFF')
    .describe('A member booking a priced session that is not from a package: OFF pays at the gym; OPTIONAL lets them pay online or at the gym; REQUIRED holds the time until they pay online'),
});
export type AppointmentPolicy = z.infer<typeof appointmentPolicySchema>;

export const pausePolicySchema = z.object({
  allowMemberSelfService: z.boolean().default(true),
  requiresStaffApproval: z.boolean().default(false),
  maxPauseDays: z.number().int().min(1).max(365).default(30),
  maxPausesPerMembership: z.number().int().min(0).max(12).default(2),
  minPauseDays: z.number().int().min(1).max(365).default(7),
});
export type PausePolicy = z.infer<typeof pausePolicySchema>;

export const accessPolicySchema = z.object({
  allowEntryWithPendingMembership: z.boolean().default(false),
  antiPassbackMinutes: z.number().int().min(0).max(24 * 60).default(0),
  newVisitAfterHours: z.number().int().min(1).max(24).default(4),
  graceDaysAfterExpiry: z.number().int().min(0).max(30).default(0),
});
export type AccessPolicy = z.infer<typeof accessPolicySchema>;

export const notificationSettingsSchema = z.object({
  sendMembershipExpiringDaysBefore: z.array(z.number().int().min(1).max(60)).default([7, 1]),
  sendClassReminderMinutesBefore: z.number().int().min(0).max(24 * 60).default(120),
  adminNotificationEmails: z.array(z.email()).default([]),
});
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;

export const billingSettingsSchema = z.object({
  autoRenewDaysBefore: z.number().int().min(1).max(14).default(3),
});
export type BillingSettings = z.infer<typeof billingSettingsSchema>;

export const cashSettingsSchema = z.object({
  requireOpenShift: z.boolean().default(false),
  barDrawer: z.boolean().default(false).describe("The bar keeps its own cash: each branch's bar has a drawer of its own, and bar cash no longer counts in the desk's"),
});
export type CashSettings = z.infer<typeof cashSettingsSchema>;

export const MAX_REFUND_APPROVAL_LIMIT_MINOR = 100_000_000;

export const refundSettingsSchema = z.object({
  approvalLimitMinor: z.number().int().min(0).max(MAX_REFUND_APPROVAL_LIMIT_MINOR).default(50_000),
});
export type RefundSettings = z.infer<typeof refundSettingsSchema>;

/** Topping up the prepaid balance online from the member area, through the gym's payment provider. */
export const walletSettingsSchema = z.object({
  onlineTopUps: z.boolean().default(true),
  minOnlineTopUpMinor: z.number().int().min(1).max(1_000_000).default(500),
  maxOnlineTopUpMinor: z.number().int().min(1).max(1_000_000).default(50_000),
});
export type WalletSettings = z.infer<typeof walletSettingsSchema>;

export const financeSettingsSchema = z.object({
  lateOnlinePayments: z
    .enum(LATE_ONLINE_PAYMENT_MODES)
    .default('FLAG')
    .describe('An online payment the bank dates inside a closed month: FLAG keeps its date and flags it on that month; NEXT_OPEN_MONTH dates it at the start of the next open month'),
});
export type FinanceSettings = z.infer<typeof financeSettingsSchema>;

export const legalSettingsSchema = z.object({
  requireTermsAtCheckout: z.boolean().default(true),
});
export type LegalSettings = z.infer<typeof legalSettingsSchema>;

/**
 * The gym's own shop window. `onlinePurchases` off stops a member buying a membership for themselves,
 * on the public site and in the member area; the front desk keeps selling either way.
 */
export const salesSettingsSchema = z.object({
  onlinePurchases: z.boolean().default(true),
  pausedMessage: z.record(z.string(), z.string().trim().max(300)).default({}),
  vatRegistered: z.boolean().default(false).describe('The gym charges VAT, so its prices include it and margins are counted on prices without it'),
  vatRatePct: z.number().min(0).max(100).default(18),
  taxId: z.string().trim().max(40).nullable().default(null).describe("The gym's identification code, printed on its quotes and invoices to companies"),
  bankAccount: z.string().trim().max(200).nullable().default(null).describe('The bank and IBAN companies pay invoices into, printed on invoices to companies'),
});
export type SalesSettings = z.infer<typeof salesSettingsSchema>;

/**
 * How the gym counts working days for staff leave. The country is the gym's own (`gyms.country`);
 * the working hours and break are shown to staff and never enter a calculation.
 */
export const leaveSettingsSchema = z.object({
  workDays: z
    .array(weekdaySchema)
    .min(1)
    .transform((days) => [...new Set(days)])
    .default(['MON', 'TUE', 'WED', 'THU', 'FRI']),
  eligibilityMonths: z.number().int().min(0).max(60).default(0),
  maxNoticeDays: z.number().int().min(1).max(730).default(92),
  observePublicHolidays: z.boolean().default(true),
  workStart: timeHHMM.default('09:00'),
  workEnd: timeHHMM.default('18:00'),
  breakStart: timeHHMM.nullable().default('13:00'),
  breakEnd: timeHHMM.nullable().default('14:00'),
});
export type LeaveSettings = z.infer<typeof leaveSettingsSchema>;

export const shiftSettingsSchema = z.object({
  swaps: z.enum(SHIFT_SWAP_MODES).default('OFF').describe('OFF: no swaps. APPROVAL: the colleague accepts, then someone with shifts.manage approves. FREE: the colleague accepting is enough.'),
  swapDepartmentIds: z.array(z.uuid()).max(100).default([]).describe('Only people in these departments may swap; empty is every department'),
  clockOutAfterHours: z.number().int().min(1).max(24).default(14).describe('A clock entry open this long is closed at the planned shift end, or left for a manager to give it an end'),
  lateAfterMinutes: z.number().int().min(0).max(120).default(5).describe('Clocking in this long after the planned start of a shift counts as late'),
});
export type ShiftSettings = z.infer<typeof shiftSettingsSchema>;

export const birthdaySettingsSchema = z.object({
  members: z.boolean().default(true).describe('Greet members on their birthday, in the app and by the channels below'),
  staff: z.boolean().default(true).describe('Greet staff on their birthday and show colleagues whose birthday it is'),
  email: z.boolean().default(true).describe('Also send the greeting by email'),
  sms: z.boolean().default(false).describe('Also send the greeting by SMS, which costs money; off until the gym admin switches it on'),
});
export type BirthdaySettings = z.infer<typeof birthdaySettingsSchema>;

export const teamSettingsSchema = z.object({
  requireValidCertificate: z
    .boolean()
    .default(false)
    .describe('Trainers and massage therapists take new classes and one-to-one sessions only with a certification on file that has not expired; off until the gym admin switches it on'),
});
export type TeamSettings = z.infer<typeof teamSettingsSchema>;

export const shiftAccessSettingsSchema = z.object({
  mode: z.enum(SHIFT_ACCESS_MODES).default('OFF').describe('OFF: shifts never limit anyone. OFF_DUTY: outside their shift, shift workers keep only their own leave, hours, shifts, onboarding, the knowledge base, kudos and their own one-to-one requests. SIGN_OUT: they keep only the clock and their own shifts, and the app signs them out'),
  graceBeforeMinutes: z.number().int().min(0).max(240).default(30).describe('How long before a rostered shift starts its working permissions begin'),
  graceAfterMinutes: z.number().int().min(0).max(240).default(15).describe('How long after a rostered shift ends its working permissions last'),
  warnBeforeMinutes: z.number().int().min(0).max(120).default(10).describe('How long before working permissions end the app warns the person'),
  roles: z
    .array(z.enum(SHIFT_ROLES))
    .max(SHIFT_ROLES.length)
    .refine((roles) => new Set(roles).size === roles.length, 'A role is listed twice')
    .default(() => [...SHIFT_ROLES])
    .describe('The roles held to their shifts; owners, admins, general and branch managers, HR, accountants and marketing never are'),
});
export type ShiftAccessSettings = z.infer<typeof shiftAccessSettingsSchema>;

export const PAYROLL_BANK_FILES = ['NONE', 'TBC_XML'] as const;
export type PayrollBankFile = (typeof PAYROLL_BANK_FILES)[number];

/** Georgian defaults; the gym's accountant confirms them before the first real run. */
export const payrollSettingsSchema = z.object({
  incomeTaxBps: z.number().int().min(0).max(10_000).default(2000),
  pensionEmployeeBps: z.number().int().min(0).max(10_000).default(200),
  pensionEmployerBps: z.number().int().min(0).max(10_000).default(200),
  pensionReducesTaxableBase: z.boolean().default(true),
  payDay: z.number().int().min(1).max(31).default(10),
  emailPayslips: z.boolean().default(true).describe('Tell each employee with a login, in the app and by email, when a payroll they are on is marked paid'),
  bankFile: z.enum(PAYROLL_BANK_FILES).default('NONE').describe("The bank's salary upload format for an approved run; NONE offers the CSV only"),
  paymentPurpose: z.string().trim().min(1).max(150).default('Salary {month}').describe('The payment description on each transfer; {month} becomes the run month, YYYY-MM'),
});
export type PayrollSettings = z.infer<typeof payrollSettingsSchema>;

/** Per role, the optional abilities whose value differs from the default. Written only through `PUT /gym/abilities`. */
export const abilityOverridesSchema = z.record(z.string(), z.record(z.string(), z.boolean()));
export type AbilityOverridesValue = z.infer<typeof abilityOverridesSchema>;

/** Per role, the permissions the gym admin set away from the role's default with its abilities. Written only through `PUT /gym/permissions`. */
export const rolePermissionOverridesSchema = z.record(z.string(), z.record(z.string(), z.boolean()));
export type RolePermissionOverridesValue = z.infer<typeof rolePermissionOverridesSchema>;

export const PRACTICE_GYM_MAX_BRANCHES = 4;
export const PRACTICE_GYM_MAX_MEMBERS = 200;

/** What a practice gym is made with; kept on the real gym, so a reset builds the same again. */
export const practiceGymMakeupSchema = z.object({
  branches: z.number().int().min(1).max(PRACTICE_GYM_MAX_BRANCHES).default(2),
  active: z.number().int().min(0).max(PRACTICE_GYM_MAX_MEMBERS).default(18).describe('Members with a membership that runs for more than a week'),
  endingSoon: z.number().int().min(0).max(PRACTICE_GYM_MAX_MEMBERS).default(4).describe('Members whose membership ends within a week'),
  expired: z.number().int().min(0).max(PRACTICE_GYM_MAX_MEMBERS).default(4),
  withoutMembership: z.number().int().min(0).max(PRACTICE_GYM_MAX_MEMBERS).default(4),
  withBalance: z.number().int().min(0).max(PRACTICE_GYM_MAX_MEMBERS).default(10).describe('How many of the members carry a bar balance, spread across them'),
});
export type PracticeGymMakeup = z.infer<typeof practiceGymMakeupSchema>;

export const practiceGymMembers = (m: PracticeGymMakeup) => m.active + m.endingSoon + m.expired + m.withoutMembership;

export const practiceGymMakeupInputSchema = practiceGymMakeupSchema.superRefine((m, ctx) => {
  const members = practiceGymMembers(m);
  if (members < 1 || members > PRACTICE_GYM_MAX_MEMBERS) ctx.addIssue({ code: 'custom', path: ['active'], message: `A practice gym has 1 to ${PRACTICE_GYM_MAX_MEMBERS} members` });
  if (m.withBalance > members) ctx.addIssue({ code: 'custom', path: ['withBalance'], message: 'More members with a balance than members' });
});

export const gymSettingsSchema = z.object({
  booking: bookingPolicySchema.default(() => bookingPolicySchema.parse({})),
  appointments: appointmentPolicySchema.default(() => appointmentPolicySchema.parse({})),
  pause: pausePolicySchema.default(() => pausePolicySchema.parse({})),
  access: accessPolicySchema.default(() => accessPolicySchema.parse({})),
  notifications: notificationSettingsSchema.default(() => notificationSettingsSchema.parse({})),
  legal: legalSettingsSchema.default(() => legalSettingsSchema.parse({})),
  sales: salesSettingsSchema.default(() => salesSettingsSchema.parse({})),
  cash: cashSettingsSchema.default(() => cashSettingsSchema.parse({})),
  refunds: refundSettingsSchema.default(() => refundSettingsSchema.parse({})),
  wallet: walletSettingsSchema.default(() => walletSettingsSchema.parse({})),
  finance: financeSettingsSchema.default(() => financeSettingsSchema.parse({})),
  billing: billingSettingsSchema.default(() => billingSettingsSchema.parse({})),
  leave: leaveSettingsSchema.default(() => leaveSettingsSchema.parse({})),
  shifts: shiftSettingsSchema.default(() => shiftSettingsSchema.parse({})),
  recruiting: recruitingSettingsSchema.default(() => recruitingSettingsSchema.parse({})),
  reviews: reviewSettingsSchema.default(() => reviewSettingsSchema.parse({})),
  knowledge: kbSettingsSchema.default(() => kbSettingsSchema.parse({})),
  payroll: payrollSettingsSchema.default(() => payrollSettingsSchema.parse({})),
  birthdays: birthdaySettingsSchema.default(() => birthdaySettingsSchema.parse({})),
  shiftAccess: shiftAccessSettingsSchema.default(() => shiftAccessSettingsSchema.parse({})),
  team: teamSettingsSchema.default(() => teamSettingsSchema.parse({})),
  openingHours: openingHoursSchema.default({}),
  memberSelfRegistration: z.boolean().default(true),
  demo: z.boolean().default(false),
  abilities: abilityOverridesSchema.default({}),
  rolePermissions: rolePermissionOverridesSchema.default({}),
  practice: practiceGymMakeupSchema.default(() => practiceGymMakeupSchema.parse({})),
});
export type GymSettings = z.infer<typeof gymSettingsSchema>;

export const gymSettingsPatchSchema = z.object({
  booking: patchOf(bookingPolicySchema).optional(),
  appointments: patchOf(appointmentPolicySchema).optional(),
  pause: patchOf(pausePolicySchema).optional(),
  access: patchOf(accessPolicySchema).optional(),
  notifications: patchOf(notificationSettingsSchema).optional(),
  legal: patchOf(legalSettingsSchema).optional(),
  sales: patchOf(salesSettingsSchema).optional(),
  cash: patchOf(cashSettingsSchema).optional(),
  refunds: patchOf(refundSettingsSchema).optional(),
  wallet: patchOf(walletSettingsSchema).optional(),
  finance: patchOf(financeSettingsSchema).optional(),
  billing: patchOf(billingSettingsSchema).optional(),
  leave: patchOf(leaveSettingsSchema).optional(),
  payroll: patchOf(payrollSettingsSchema).optional(),
  birthdays: patchOf(birthdaySettingsSchema).optional(),
  shiftAccess: patchOf(shiftAccessSettingsSchema).optional(),
  team: patchOf(teamSettingsSchema).optional(),
  openingHours: openingHoursSchema.optional(),
  memberSelfRegistration: z.boolean().optional(),
});
export type GymSettingsPatch = z.infer<typeof gymSettingsPatchSchema>;

export const createGymSchema = z.object({
  name: z.string().trim().min(2).max(120),
  slug: slugSchema,
  legalName: z.string().trim().max(200).optional(),
  country: z.string().length(2).toUpperCase().default('GE'),
  city: z.string().trim().max(120).optional(),
  currency: currencySchema.default('GEL'),
  timezone: timezoneSchema.default('Asia/Tbilisi'),
  defaultLocale: localeSchema.default('en'),
  ownerEmail: z.email().toLowerCase().optional(),
});
export type CreateGymInput = z.infer<typeof createGymSchema>;

export const updateGymSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  legalName: z.string().trim().max(200).nullable().optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  address: z.string().trim().max(300).nullable().optional(),
  city: z.string().trim().max(120).nullable().optional(),
  country: z.string().length(2).toUpperCase().optional(),
  timezone: timezoneSchema.optional(),
  currency: currencySchema.optional(),
  defaultLocale: localeSchema.optional(),
  phone: z.string().trim().max(32).nullable().optional(),
  email: z.email().nullable().optional(),
  branding: patchOf(brandingSchema).optional(),
  settings: gymSettingsPatchSchema.optional(),
});
export type UpdateGymInput = z.infer<typeof updateGymSchema>;

export const gymSchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: z.string(),
  legalName: z.string().nullable(),
  description: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string(),
  timezone: z.string(),
  currency: z.string(),
  defaultLocale: localeSchema,
  phone: z.string().nullable(),
  email: z.string().nullable(),
  status: z.enum(GYM_STATUSES),
  branding: brandingSchema,
  settings: gymSettingsSchema,
  onboarding: z.record(z.string(), z.boolean()),
  activatedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type GymDto = z.infer<typeof gymSchema>;

export const publicGymSchema = gymSchema.pick({
  id: true,
  name: true,
  slug: true,
  description: true,
  address: true,
  city: true,
  country: true,
  timezone: true,
  currency: true,
  defaultLocale: true,
  phone: true,
  email: true,
  branding: true,
});
export type PublicGymDto = z.infer<typeof publicGymSchema>;

export const gymDomainSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  hostname: z.string(),
  type: z.enum(DOMAIN_TYPES),
  verificationStatus: z.enum(DOMAIN_VERIFICATION_STATUSES),
  verificationToken: z.string().nullable(),
  isPrimary: z.boolean(),
  verifiedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type GymDomainDto = z.infer<typeof gymDomainSchema>;

export const createGymDomainSchema = z.object({
  hostname: z
    .string()
    .trim()
    .toLowerCase()
    .min(3)
    .max(253)
    .regex(/^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/, 'Invalid hostname'),
  isPrimary: z.boolean().default(false),
});
export type CreateGymDomainInput = z.infer<typeof createGymDomainSchema>;

export const gymUserSchema = z.object({
  id: idSchema,
  userId: idSchema,
  gymId: idSchema,
  role: z.enum(ROLES),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  createdAt: z.string(),
  locationIds: z.array(idSchema).describe('The branches this person works at; empty means every branch'),
});
export type GymUserDto = z.infer<typeof gymUserSchema>;

export const practiceGymSchema = z.object({
  gymId: idSchema.describe("The practice gym's id; send it as X-Gym-Id to work in it"),
  createdAt: z.string(),
  members: z.number().int(),
  active: z.number().int().describe('Members with an active membership that runs for more than a week'),
  endingSoon: z.number().int().describe('Members whose active membership ends within a week'),
  expired: z.number().int().describe('Members with no active membership and an expired one'),
  withoutMembership: z.number().int().describe('Everyone else'),
  withBalance: z.number().int(),
  branches: z.number().int(),
});
export type PracticeGymDto = z.infer<typeof practiceGymSchema>;

export const practiceGymStateSchema = z.object({
  practice: practiceGymSchema.nullable().describe('The live practice gym as it is now, or null'),
  makeup: practiceGymMakeupSchema.describe('What it is built with on the next create or reset'),
});
export type PracticeGymStateDto = z.infer<typeof practiceGymStateSchema>;

export const staffLocationsSchema = z.object({
  locationIds: z.array(idSchema).max(100).describe('Empty: every branch'),
});
export type StaffLocationsInput = z.infer<typeof staffLocationsSchema>;

export const inviteGymUserSchema = z.object({
  email: z.email().toLowerCase(),
  role: z.enum(STAFF_ROLES),
  firstName: z.string().trim().max(80).optional(),
  lastName: z.string().trim().max(80).optional(),
  locationIds: z.array(idSchema).max(100).default([]).describe('The branches they work at: at least one, except for owners and admins, who work at every branch and take none'),
});
export type InviteGymUserInput = z.infer<typeof inviteGymUserSchema>;

export const staffInvitationSchema = z.object({
  id: idSchema,
  email: z.string(),
  role: z.enum(ROLES),
  locationIds: z.array(idSchema).describe('The branches they will work at once they accept; empty for owners and admins'),
  invitedByName: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
});
export type StaffInvitationDto = z.infer<typeof staffInvitationSchema>;

export const roleAbilitySchema = z.object({
  key: z.enum(ABILITY_KEYS),
  permissions: z.array(z.enum(PERMISSIONS)),
  defaultEnabled: z.boolean(),
  enabled: z.boolean(),
});
export type RoleAbilityDto = z.infer<typeof roleAbilitySchema>;

export const roleAbilitiesSchema = z.object({
  role: z.enum(SWITCHABLE_ROLES),
  abilities: z.array(roleAbilitySchema),
});
export type RoleAbilitiesDto = z.infer<typeof roleAbilitiesSchema>;

export const setAbilitySchema = z.object({
  role: z.string().trim().min(1).max(40),
  ability: z.string().trim().min(1).max(80),
  enabled: z.boolean(),
});
export type SetAbilityInput = z.infer<typeof setAbilitySchema>;

export const permissionCatalogEntrySchema = z.object({
  key: z.enum(PERMISSIONS),
  module: z.enum(PERMISSION_MODULES),
  risk: z.enum(PERMISSION_RISKS),
  requires: z.array(z.enum(PERMISSIONS)),
  grantableTo: z.enum(PERMISSION_GRANTS),
  locked: z.boolean(),
});
export type PermissionCatalogEntryDto = z.infer<typeof permissionCatalogEntrySchema>;

export const permissionCellSchema = z.object({ granted: z.boolean(), source: z.enum(PERMISSION_SOURCES) });
export type PermissionCellDto = z.infer<typeof permissionCellSchema>;

export const permissionMatrixSchema = z.object({
  catalog: z.array(permissionCatalogEntrySchema),
  roles: z.array(z.enum(PERMISSION_MATRIX_ROLES)),
  matrix: z.record(z.enum(PERMISSION_MATRIX_ROLES), z.record(z.string(), permissionCellSchema)).describe('Per role, every permission the gym admin can set there, with whether the role holds it and why'),
});
export type PermissionMatrixDto = z.infer<typeof permissionMatrixSchema>;

export const setRolePermissionsSchema = z
  .object({
    role: z.enum(PERMISSION_MATRIX_ROLES),
    changes: z
      .array(z.object({ permission: z.enum(PERMISSIONS), granted: z.boolean().nullable().describe('null takes the tick back to the role default') }))
      .min(1)
      .max(PERMISSIONS.length),
  })
  .refine((input) => new Set(input.changes.map((c) => c.permission)).size === input.changes.length, { path: ['changes'], message: 'Each permission at most once' });
export type SetRolePermissionsInput = z.infer<typeof setRolePermissionsSchema>;
